import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { defaultEmptyLevels } from '@shared/config/variable-scale-manager-config';
import { ThresholdsFetchParam } from '@shared/models/variable-scale-fetch-param.model';
import {
  AsymmetrySeverityWeightsEnum,
  AsymmetrySeverityWeightsType,
  AsymmetryVariableRangesClass,
  RangeItem,
} from '@sleipai/sleip-models';
import {
  AsymmetryVariableRangesSelectors,
  AsymmetryVariableRangesStore,
  AsymmetryVariableRangesStoreFacade,
} from '@sleipai/sleip-ngrx-store';
import { distinctUntilChanged, filter } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class VariableScaleManagerService {
  private asymmetryVariableRangesStoreFacade = inject(
    AsymmetryVariableRangesStoreFacade,
  );

  private variableRangesStore = inject(
    Store<AsymmetryVariableRangesStore.State>,
  );

  destroyRef = inject(DestroyRef);

  private readonly asymmetryVariableRanges =
    toSignal<AsymmetryVariableRangesClass>(
      this.asymmetryVariableRangesStoreFacade
        .getAsymmetryVariableRangesById$('asymmetryVariableSeverityThresholds')
        .pipe(
          filter((data) => data != null),
          takeUntilDestroyed(this.destroyRef),
        ),
    );

  private readonly asymmetryVariableSdtvRanges =
    toSignal<AsymmetryVariableRangesClass>(
      this.asymmetryVariableRangesStoreFacade
        .getAsymmetryVariableRangesById$('asymmetryVariableStdvLevelThresholds')
        .pipe(
          filter((data) => data != null),
          takeUntilDestroyed(this.destroyRef),
        ),
    );

  private readonly areRangesLoadedInStore = toSignal<boolean>(
    this.variableRangesStore
      .select(AsymmetryVariableRangesSelectors.selectIsLoaded)
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)),
  );

  readonly areRangesLoaded = computed(() => {
    if (
      this.areRangesLoadedInStore() &&
      this.asymmetryVariableRanges() &&
      this.asymmetryVariableSdtvRanges()
    ) {
      return true;
    } else {
      return false;
    }
  });

  public getAsymmetryLevels(
    thresholdParams: ThresholdsFetchParam,
  ): Array<number> {
    const thresholdRangeItemList =
      this.getAsymmetrySeverityThresholdsRangeItemList(thresholdParams);
    if (!thresholdRangeItemList || thresholdRangeItemList.length === 0) {
      return defaultEmptyLevels;
    } else {
      return thresholdRangeItemList.map((item) => item.high ?? 0);
    }
  }

  public getAsymmetrySeverityThresholdsRangeItemList(
    thresholdParams: ThresholdsFetchParam,
  ): RangeItem[] {
    const {
      bodyPart,
      asymmetryScalingType,
      strideMeanType,
      asymmetryAnalysisType,
    } = thresholdParams;
    const thresholdLevelData =
      this.asymmetryVariableRanges()?.[asymmetryAnalysisType][
        asymmetryScalingType
      ];
    if (!thresholdLevelData) return [];

    const bodayPartThresholdLevelData = thresholdLevelData[bodyPart];
    if (!bodayPartThresholdLevelData) return [];

    const asymmetryStrideMeanLevelData =
      bodayPartThresholdLevelData[strideMeanType];

    if (!asymmetryStrideMeanLevelData) return [];

    const { items } = asymmetryStrideMeanLevelData;
    return items;
  }

  public getMaxLevel(thresholdParams: ThresholdsFetchParam): number {
    const graphSections = 4;

    const asymmetryLevels = this.getAsymmetryLevels(thresholdParams);

    if (!asymmetryLevels || asymmetryLevels.length === 0) return 1;

    const firstLevel = asymmetryLevels[1] * graphSections;
    return firstLevel;
  }

  public getAsymmetrySeverityTypeForMean(
    limbAsymmetryMean: number,
    thresholdParams: ThresholdsFetchParam,
  ) {
    const asymmetryLevels = this.getAsymmetryLevels(thresholdParams);
    let asymmetrySeverityWeight;
    limbAsymmetryMean = Math.abs(limbAsymmetryMean);
    if (0 <= limbAsymmetryMean && limbAsymmetryMean < asymmetryLevels[0]) {
      asymmetrySeverityWeight = AsymmetrySeverityWeightsEnum.sound;
    } else if (
      asymmetryLevels[0] <= limbAsymmetryMean &&
      limbAsymmetryMean < asymmetryLevels[1]
    ) {
      asymmetrySeverityWeight = AsymmetrySeverityWeightsEnum.veryMild;
    } else if (
      asymmetryLevels[1] <= limbAsymmetryMean &&
      limbAsymmetryMean < asymmetryLevels[2]
    ) {
      asymmetrySeverityWeight = AsymmetrySeverityWeightsEnum.mild;
    } else if (
      asymmetryLevels[2] <= limbAsymmetryMean &&
      limbAsymmetryMean < asymmetryLevels[3]
    ) {
      asymmetrySeverityWeight = AsymmetrySeverityWeightsEnum.moderate;
    } else if (
      asymmetryLevels[3] <= limbAsymmetryMean &&
      limbAsymmetryMean < asymmetryLevels[4]
    ) {
      asymmetrySeverityWeight = AsymmetrySeverityWeightsEnum.severe;
    } else if (
      asymmetryLevels[4] <= limbAsymmetryMean &&
      limbAsymmetryMean < Infinity
    ) {
      asymmetrySeverityWeight = AsymmetrySeverityWeightsEnum.severe;
    } else {
      asymmetrySeverityWeight = AsymmetrySeverityWeightsEnum.sound;
    }

    return asymmetrySeverityWeight;
  }

  public getAsymmetryLimbColor(
    asymmetryLimbMaxWeightValue: AsymmetrySeverityWeightsType,
    thresholdParams: ThresholdsFetchParam,
  ) {
    const rangeItemList =
      this.getAsymmetrySeverityThresholdsRangeItemList(thresholdParams);

    const rangeDictionary: Record<number, RangeItem> = {};
    for (const item of rangeItemList) {
      rangeDictionary[item.index] = item;
    }
    const level0 = rangeDictionary[0]?.color;
    const level1 = rangeDictionary[1]?.color;
    const level2 = rangeDictionary[2]?.color;
    const level3 = rangeDictionary[3]?.color;
    const level4 = rangeDictionary[4]?.color;

    switch (asymmetryLimbMaxWeightValue) {
      case AsymmetrySeverityWeightsEnum.sound:
        return level0;
      case AsymmetrySeverityWeightsEnum.veryMild:
        return level1;
      case AsymmetrySeverityWeightsEnum.mild:
        return level2;
      case AsymmetrySeverityWeightsEnum.moderate:
        return level3;
      case AsymmetrySeverityWeightsEnum.severe:
        return level4;
      default:
        return level0;
    }
  }

  public getAsymmetrySdtvLevelThresholdsRangeItemList(
    thresholdParams: ThresholdsFetchParam,
  ) {
    const {
      bodyPart,
      asymmetryScalingType,
      strideMeanType,
      asymmetryAnalysisType,
    } = thresholdParams;
    const sdtvLevelThresholdData =
      this.asymmetryVariableSdtvRanges()?.[asymmetryAnalysisType][
        asymmetryScalingType
      ];
    if (!sdtvLevelThresholdData) return [];

    const bodayPartSdtvLevelThresholdData = sdtvLevelThresholdData[bodyPart];
    if (!bodayPartSdtvLevelThresholdData) return [];

    const asymmetryStrideMeanSdtvLevelData =
      bodayPartSdtvLevelThresholdData[strideMeanType];

    if (!asymmetryStrideMeanSdtvLevelData) return [];

    const { items } = asymmetryStrideMeanSdtvLevelData;
    return items;
  }
}
